'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { useCreateInvoice } from '@/hooks/use-invoices';
import { useDirectorParents } from '@/hooks/use-director-data';

interface InvoiceFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

export function InvoiceFormModal({
  open,
  onOpenChange,
  onSuccess,
}: InvoiceFormModalProps) {
  const [parentUserId, setParentUserId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: '', quantity: 1, unitPrice: 0 },
  ]);
  const [error, setError] = useState('');

  const createInvoice = useCreateInvoice();
  const { data: parentsData, isLoading: parentsLoading } = useDirectorParents();

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setParentUserId('');
      setDueDate('');
      setDescription('');
      setLineItems([{ description: '', quantity: 1, unitPrice: 0 }]);
      setError('');
    }
  }, [open]);

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: string | number) => {
    const updated = [...lineItems];
    if (field === 'description') {
      updated[index].description = value as string;
    } else if (field === 'quantity') {
      updated[index].quantity = Math.max(1, Number(value) || 1);
    } else if (field === 'unitPrice') {
      // Store in cents for consistency with amountCents
      updated[index].unitPrice = Math.round(Math.max(0, Number(value) || 0) * 100);
    }
    setLineItems(updated);
  };

  const calculateTotal = (): number => {
    return lineItems.reduce((sum, item) => {
      return sum + (item.quantity * item.unitPrice); // unitPrice already in cents
    }, 0);
  };

  const formatCurrency = (cents: number): string => {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!parentUserId) {
      setError('Please select a parent');
      return;
    }

    if (!dueDate) {
      setError('Please set a due date');
      return;
    }

    const validLineItems = lineItems.filter(item => item.description && item.unitPrice > 0);
    if (validLineItems.length === 0) {
      setError('Please add at least one line item with a description and price');
      return;
    }

    const amountCents = calculateTotal();
    if (amountCents <= 0) {
      setError('Total amount must be greater than zero');
      return;
    }

    try {
      await createInvoice.mutateAsync({
        parentUserId,
        amountCents,
        currency: 'aud',
        dueDate,
        description: description || undefined,
        lineItems: validLineItems,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invoice');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Invoice</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="parent">Parent *</Label>
            <Select value={parentUserId} onValueChange={setParentUserId}>
              <SelectTrigger>
                <SelectValue placeholder={parentsLoading ? 'Loading...' : 'Select parent'} />
              </SelectTrigger>
              <SelectContent>
                {parentsData?.parents.map((parent) => (
                  <SelectItem key={parent.id} value={parent.id}>
                    {parent.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dueDate">Due Date *</Label>
            <Input
              id="dueDate"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Invoice description (optional)"
              rows={2}
            />
          </div>

          {/* Line Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Line Items *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="w-4 h-4 mr-1" />
                Add Item
              </Button>
            </div>
            <div className="space-y-2">
              {lineItems.map((item, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <Input
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                    min={1}
                    className="w-16"
                  />
                  <Input
                    type="number"
                    placeholder="Price"
                    value={item.unitPrice ? (item.unitPrice / 100).toFixed(2) : ''}
                    onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                    min={0}
                    step={0.01}
                    className="w-24"
                  />
                  {lineItems.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLineItem(index)}
                      className="text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="flex justify-end border-t pt-4">
            <div className="text-right">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold">{formatCurrency(calculateTotal())}</p>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createInvoice.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createInvoice.isPending}>
              {createInvoice.isPending ? 'Creating...' : 'Create Invoice'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
